import { orderBy } from "es-toolkit";
import { makeAutoObservable, runInAction } from "mobx";

import { getCurrentUserNotifications, markNotificationsAsRead } from "@/api";
import { onCableMessage } from "@/api/cable";

import type { Notification } from "@/api";
import type { RootStore } from "@/store/RootStore";
import type { LazyStore } from "@/store/types";

export class NotificationsStore implements LazyStore {
  private rootStore: RootStore;
  private fetchMutexId = 0;
  private markingReadIds = new Set<string>();

  notifications = new Map<string, Notification>();

  isLoading = false;

  hasMore = true;

  private cableUnsubscribe?: () => void;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<NotificationsStore, "fetchMutexId" | "markingReadIds">(this, {
      fetchMutexId: false,
      markingReadIds: false,
    });
  }

  lazyEnable() {
    this.cableSubscribe();
  }

  get list(): Notification[] {
    return orderBy(Array.from(this.notifications.values()), ["cursor"], ["desc"]);
  }

  get unreadCount(): number {
    return this.rootStore.auth.userData?.unreadNotificationsCount ?? 0;
  }

  async fetch(signal?: AbortSignal): Promise<void> {
    await this.doFetch(signal, undefined, () => {
      // we don't know how many notifications we missed while tab was suspended, starting fresh
      this.notifications.clear();
    });
  }

  async fetchMore(signal?: AbortSignal): Promise<boolean> {
    if (!this.hasMore) return false;
    const oldest = this.list.at(-1);
    if (!oldest) return false;

    return this.doFetch(signal, oldest.cursor);
  }

  private async doFetch(
    signal?: AbortSignal,
    cursor?: string,
    successCallback?: () => void,
  ): Promise<boolean> {
    const fetchId = ++this.fetchMutexId;

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      const { notifications, hasMore } = await getCurrentUserNotifications({ cursor }, { signal });
      if (signal?.aborted || fetchId !== this.fetchMutexId) return false;
      runInAction(() => {
        successCallback?.();
        for (const notification of notifications) {
          this.notifications.set(notification.id, notification);
        }
        this.hasMore = hasMore;
      });
      return true;
    } finally {
      if (fetchId === this.fetchMutexId) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  markOneAsRead(id: string): void {
    this.markAsRead([id]).catch((err) => console.error("Notifications: mark as read failed", err));
  }

  async markAsRead(ids: string[]): Promise<void> {
    const toMark = ids.filter(
      (id) => this.notifications.get(id)?.isRead === false && !this.markingReadIds.has(id),
    );
    if (toMark.length === 0) return;

    toMark.forEach((id) => this.markingReadIds.add(id));
    try {
      await markNotificationsAsRead({ ids: toMark });

      runInAction(() => {
        for (const id of toMark) {
          const n = this.notifications.get(id);
          if (n && !n.isRead) {
            this.notifications.set(id, { ...n, isRead: true });
          }
        }
      });
    } finally {
      toMark.forEach((id) => this.markingReadIds.delete(id));
    }
  }

  upsert(notification: Notification): void {
    if (notification.isDeleted) {
      this.notifications.delete(notification.id);
    } else {
      this.notifications.set(notification.id, notification);
    }
  }

  private cableSubscribe(): void {
    this.cableUnsubscribe?.();
    this.cableUnsubscribe = onCableMessage((payload) => {
      if (payload.type === "notification_updated") {
        this.upsert(payload.notification);
      }
    });
  }

  dispose(): void {
    this.cableUnsubscribe?.();
    this.cableUnsubscribe = undefined;
    this.notifications.clear();
    this.markingReadIds.clear();
    this.hasMore = true;
  }
}
